<template>
  <v-dialog
    v-if="room"
    ref="dialog"
    v-model="dialogOpened"
    max-width="500"
    scrollable
    persistent
    no-click-animation
    :retain-focus="false"
    @keydown.esc="hide"
  >
    <v-card>
      <v-btn class="v-dialog--close" icon @click="hide">
        <v-icon>fal fa-times</v-icon>
      </v-btn>
      <v-card-title class="headline pr-10">
        Benutzer in Raum <i>{{room.name}}</i> einladen
      </v-card-title>

      <v-card-text>

        <div v-if="loading" class="text-center">
          <v-progress-circular
            class="mt-4 mb-4"
            color="primary"
            size="30"
            indeterminate
          ></v-progress-circular>
          <div>Einladungs-Link wird erstellt ...</div>
        </div>

        <v-alert v-if="error" type="error" class="mb-0" text>
          Einladungs-Link konnte nicht erstellt werden.
        </v-alert>

        <div v-show="!loading && !error"><!--  use v-show for canvas -->
          <v-alert type="info" color="accent" text>
            Sende den Einladungs-Link an einem anderen Benutzer.<br>
            Nachdem der Empfänger den Link aufgerufen hat, ist er
            dem Raum beigetreten und ihr könnt euch auch außerhalb des selben
            Netzwerks in diesem Raum wiederfinden.<br>
            <small>
              <strong>Hinweis:</strong> Der Einladungs-Link ist nur einmalig
              und für maximal 14 Tage gültig.
            </small>
          </v-alert>

          <h4>Variante 1: <span class="font-weight-regular">Link an E-Mail Adresse senden</span></h4>
          <v-form
            ref="emailForm"
            :readonly="sending"
            lazy-validation
            @submit.prevent="_sendEmail"
          >
            <input type="hidden" name="invitationId" :value="invitationId" />
            <v-text-field
              v-model="email"
              class="mt-6"
              name="email"
              placeholder="E-Mail Adresse eingeben"
              label="E-Mail Adresse"
              :rules="[rules.required, rules.email]"
              :error-messages="sending_errorMessage"
              :prepend-inner-icon="$vuetify.breakpoint.smAndUp ? 'far fa-at' : ''"
              :append-outer-icon="!sending ? 'far fa-paper-plane' : 'far fa-spinner-third fa-spin'"
              :readonly="sending"
              outlined
              @click:append-outer.prevent="_sendEmail"
            ></v-text-field>
          </v-form>

          <h4>Variante 2: <span class="font-weight-regular">Link selbst versenden</span></h4>
          <canvas ref="qrCode"></canvas>
          <code class="d-block text-center py-2 px-2" @click="_copy">{{href}}</code>
        </div>
      </v-card-text>

      <v-card-actions v-if="!loading && !error">
        <v-spacer></v-spacer>
        <v-btn :color="!shareable ? 'accent' : ''" text @click="_copy">
          Link kopieren
        </v-btn>
        <v-btn v-if="shareable" color="accent" text @click="_share">
          Teilen
        </v-btn>
      </v-card-actions>

      <v-card-actions v-else-if="error && !loading">
        <v-spacer></v-spacer>
        <v-btn text @click="hide">Schließen</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  import QRCode from 'qrcode';

  import {copyTextToClipboard, serializeForm} from '../../assets/js/utils';
  import eventBus from '../../assets/js/event-bus';
  import Dialog from './Dialog';


  export default {
    name: 'RoomInviteUserDialog',

    extends: Dialog,

    data() {
      return {
        room: {},
        loading: false,
        error: false,
        invitationId: '',
        invitationUsed: false,
        sending: false,
        sending_errorMessage: '',
        email: '',
        rules: {
          required: (value) => {
            return !!value || 'E-Mail erforderlich';
          },
          email: (value) => {
            const input = document.createElement('input');
            input.type = 'email';
            input.value = value;
            let valid;
            try {
              valid = input.validity.valid;
            } catch (error) {
              valid = true;
            }
            return !!valid || 'E-Mail ungültig';
          }
        }
      };
    },

    computed: {
      currentRoom() {
        return this.$store.getters['share/getRoom'];
      },

      href() {
        return `${location.origin}/invitation/${this.invitationId}`;
      },

      shareable() {
        return typeof navigator.share === 'function';
      }
    },

    watch: {
      dialogOpened(opened) {
        if (opened) {
          this.$nextTick(() => this._createInvitation());
        } else {
          this.$nextTick(() => this.$refs.emailForm.resetValidation());
        }
      },

      currentRoom(room) {
        this.room = room;
      }
    },

    mounted() {
      eventBus.$on('roomInviteUser', this._onRoomInviteUser);
    },

    destroyed() {
      eventBus.$off('roomInviteUser', this._onRoomInviteUser);
    },

    methods: {
      show(opt_room) {
        this.room = opt_room || this.currentRoom;
        Dialog.methods.show.call(this);
      },

      async hide() {
        Dialog.methods.hide.call(this);
        if (this.invitationId && !this.invitationUsed) {
          try {
            await this.$api.post('InvitationDelete', {invitationId: this.invitationId});
            this.invitationId = '';
          } catch (error) {
            /* NOOP */
          }
        }
      },

      async _copy() {
        try {
          await copyTextToClipboard(this.href);
          this.invitationUsed = true;
          eventBus.$emit('notifyUser', 'Link in die Zwischenablage kopiert');
        } catch (error) {
          eventBus.$emit('notifyUser', 'Link konnte nicht in die Zwischenablage kopiert werden');
        }
      },

      async _share() {
        if (!this.shareable) {
          return;
        }
        try {
          const {userName} = this.$store.getters['share/getPeerSelf'].name;
          const title = 'XidrasTransfer Einladung';

          await navigator.share({
            title,
            text: `${title}

${userName} hat dich in den Raum "${this.room.name}" eingeladen.

Klicke auf den Link um die Einladung anzunehmen:
${this.href}

Die Einladung ist nur einmalig und für maximal 14 Tage gültig.
Nachdem du einem Raum beigetreten bist, kannst du diesen auch jederzeit wieder verlassen.
Falls du nicht beitreten möchtest kannst du diese Nachricht löschen.`
          });
          this.invitationUsed = true;
          this.hide();
        } catch (error) {
          /* NOOP */
        }
      },

      async _sendEmail() {
        if (this.sending) {
          return;
        }

        const form = this.$refs.emailForm;
        const valid = form.validate();
        if (!valid) {
          return;
        }

        this.sending = true;
        this.sending_errorMessage = '';

        try {
          const data = serializeForm(form.$el);
          await this.$api.post('InvitationSendEmail', data);
          this.invitationUsed = true;
          this.email = '';
          this.hide();
          eventBus.$emit('notifyUser', 'Einladung versendet');

        } catch (error) {
          let message = 'E-Mail konnte nicht versendet werden. Versuche es später erneut.';
          const errorStatus = error.response.status;
          if (errorStatus === 400) {
            try {
              const errorCode = error.response.data.error.innererror.code;
              switch (errorCode) {
                case 'E_INVITATION_ALREADY_SENT':
                  message = 'Diese Einladung wurde bereits an eine E-Mail versendet.';
                  break;

                case 'E_EMAIL_ALREADY_INVITED':
                  message = 'Diese E-Mail Adresse wurde bereits eingeladen.';
                  break;

                case 'E_USER_ALREADY_JOINED':
                  message = 'Diese E-Mail Adresse ist bereits beigetreten.';
                  break;

                default:
                  // Use default message.
                  break;
              }

            } catch (err) {
              /* NOOP - use default error messge. */
            }
          }
          this.sending_errorMessage = message;
        }

        this.sending = false;
      },

      async _createInvitation() {
        this.error = false;
        this.loading = true;
        this.invitationUsed = false;
        this.sending_errorMessage = '';

        try {
          this.invitationId = await this._getInvitationId();
          await this._generateQrCode();
          this.loading = false;

        } catch (error) {
          this.invitationId = '';
          this.error = true;
          this.loading = false;
        }
      },

      async _getInvitationId() {
        const response = await this.$api.post('InvitationCreate', {roomId: this.room.id});
        return response.data.invitationId;
      },

      async _generateQrCode() {
        if (this._qrCode === this.href) {
          return;
        }
        await QRCode.toCanvas(this.$refs.qrCode, this.href);
        this._qrCode = this.href;
      },

      _onRoomInviteUser(room) {
        this.show(room);
      }
    }
  };
</script>

<style scoped>
  canvas {
    display: block;
    margin: 0 auto;
  }

  code {
    color: inherit;
    white-space: nowrap;
    overflow-x: auto;
    box-shadow: none;
  }
</style>
